<template>
    <div class="mt-4">
        <h5 class="font-weight-bolder">Admission Number</h5>
        <p class="text-muted">
            Admission numbers are generated automatically when an admission form application is fully approved,
            in the format <strong>{{ form.with_slash ? 'Prefix/YYYY/Number' : 'PrefixYYYYNumber' }}</strong>. Once saved, editing needs an OTP from
            ezygo support.
        </p>

        <span v-if="loading" class="text-warning">Loading settings...</span>

        <div v-else>
            <b-form-group>
                <b-form-checkbox v-model="form.auto_assign" :disabled="locked" inline>
                    Enable auto assign of admission number
                </b-form-checkbox>
            </b-form-group>

            <b-form-group label="Prefix">
                <b-form-input v-model.trim="form.inst_code" :disabled="locked" placeholder="e.g. EZY" />
            </b-form-group>

            <b-form-group label="Starting No.">
                <b-form-input v-model.trim="form.starting_no" :disabled="locked" maxlength="6" placeholder="e.g. 001" />
                <small class="text-muted">Up to 6 digits. Leading zeros are kept, e.g. 001 gives 001, 002, 003...</small>
            </b-form-group>

            <b-form-group label="Format">
                <b-form-radio v-model="form.with_slash" :value="true" :disabled="locked">
                    <strong>{{ preview(true) }}</strong>
                </b-form-radio>
                <b-form-radio v-model="form.with_slash" :value="false" :disabled="locked" class="mt-1">
                    <strong>{{ preview(false) }}</strong>
                </b-form-radio>
                <small class="text-muted d-block mt-1">
                    {{ canSave ? 'Shows the next number in each format.' : 'Example shown until a prefix and starting no. are entered.' }}
                    Applies to numbers generated after saving.
                </small>
            </b-form-group>

            <b-button v-if="!status.configured" variant="primary" :disabled="saving || !canSave" @click="save">
                Save
            </b-button>

            <b-button v-else-if="locked" variant="outline-primary" :disabled="saving" @click="requestEdit">
                Edit
            </b-button>

            <div v-else>
                <b-form-group>
                    <label class="required-label">Enter OTP to save changes:</label>
                    <b-form-input v-model.trim="otp" placeholder="Enter OTP" />
                </b-form-group>
                <p class="text-danger small">Please contact ezygo support for OTP</p>

                <b-button variant="success" :disabled="saving || !canSave || !otp" @click="save">
                    Verify OTP & Save
                </b-button>
                <b-button variant="danger" class="ml-2" :disabled="saving" @click="cancelEdit">
                    Cancel
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            loading: false,
            saving: false,
            editing: false,
            otp: '',
            status: { configured: false, auto_assign: false, inst_code: null, starting_no: null, next_no: null, with_slash: true },
            form: { auto_assign: true, inst_code: '', starting_no: '', with_slash: true },
        };
    },

    computed: {
        locked() {
            return this.status.configured && !this.editing;
        },
        canSave() {
            return /^[A-Za-z0-9_-]+$/.test(this.form.inst_code) && /^\d{1,6}$/.test(this.form.starting_no);
        },
    },

    created() {
        this.loadSettings();
    },

    methods: {
        // the next number in the given format, or an example until the form is filled in
        preview(withSlash) {
            const separator = withSlash ? '/' : '';
            const year = new Date().getFullYear();
            if (!this.canSave) return `EZY${separator}${year}${separator}001`;

            // numbering continues from next_no unless the starting no. is changed
            const number = this.status.configured && this.form.starting_no === this.status.starting_no
                ? this.status.next_no
                : parseInt(this.form.starting_no, 10);
            const padded = String(number).padStart(this.form.starting_no.length, '0');
            return `${this.form.inst_code}${separator}${year}${separator}${padded}`;
        },

        async loadSettings() {
            this.loading = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/admissionnumberconfig`;
                const response = await this.$axios.get(url);
                this.status = response.data;
                this.resetForm();
            } catch (error) {
                this.$toast.error('Failed to load admission number settings', {
                    position: 'top',
                    duration: 4000,
                });
            } finally {
                this.loading = false;
            }
        },

        resetForm() {
            // new setups start enabled; saved ones show what was saved
            this.form.auto_assign = this.status.configured ? this.status.auto_assign : true;
            this.form.inst_code = this.status.inst_code || '';
            this.form.starting_no = this.status.starting_no || '';
            this.form.with_slash = this.status.with_slash !== false;
        },

        async requestEdit() {
            if (!confirm('Editing the admission number settings needs an OTP from ezygo support. Continue?')) return;

            this.saving = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/admissionnumberconfig/send-edit-otp`;
                await this.$axios.post(url);
                this.editing = true;
            } catch (error) {
                this.$toast.error('Failed to send OTP', {
                    position: 'top',
                    duration: 4000,
                });
            } finally {
                this.saving = false;
            }
        },

        cancelEdit() {
            this.editing = false;
            this.otp = '';
            this.resetForm();
        },

        async save() {
            this.saving = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/institution/admissionnumberconfig`;
                await this.$axios.post(url, { ...this.form, otp: this.otp || null });

                this.$toast.success('Admission number settings saved successfully.', {
                    position: 'top',
                    duration: 4000,
                });

                this.editing = false;
                this.otp = '';
                await this.loadSettings();
            } catch (error) {
                this.$toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to save admission number settings.', {
                    position: 'top',
                    duration: 4000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
